import { formatCurrency } from "../../utils";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css"; // Required styles
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import SkeletonCard from "../skeletons/SkeletonCard";
import CardActiveBotSkeleton from "../../components/cards/skeletons/CardActiveBotSkeleton";
import CardActiveBot from "../../components/cards/CardActiveBot";
import DrawerActivateBot from "../drawers/DrawerActivateBot";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useBot } from "../../context/botContext";
import BotLogo from "../commons/BotLogo";

const PlusCircleIcon = () => (
  <svg
    className="h-8 w-8 text-teal-500"
    fill="none"
    viewBox="0 0 24 24"
    stroke="currentColor"
    strokeWidth={2}
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

export const IconBot = ({ src, alt, className }) => (
  <img
    src={src}
    alt={alt || "avatar"}
    className={`w-full max-w-[40px] h-[40px] xl:max-w-[45px] xl:h-[45px] rounded-full border-2 border-white object-cover ${
      className || ""
    }`}
  />
);

// Card for activating a new bot
const ActivateNewBotCard = ({ setOpenDialogBot }) => (
  <div
    className="w-full md:max-w-[175px] p-4 bg-white rounded-lg flex flex-col items-center justify-center min-w-[130px] border border-dashed border-gray-300 text-center cursor-pointer hover:bg-gray-100 transition"
    onClick={() => setOpenDialogBot("step-bot-1")}
  >
    <p className="text-xs font-medium text-gray-700 mb-2">Activate New Bot</p>
    <div className="flex -space-x-2 mb-3">
      <BotLogo
        iconColor="bg-green-200"
        className="w-[45px] h-[45px] !justify-center !flex !items-center"
        size={42}
      />
      <BotLogo
        iconColor="bg-[#FAC482]"
        className="w-[45px] h-[45px] !justify-center !flex !items-center"
        size={42}
      />
      <BotLogo
        iconColor="bg-[#B2B7DE]"
        className="w-[45px] h-[45px] !justify-center !flex !items-center"
        size={42}
      />
      {/* <IconBot src="/images/icons/icon_bot_rebalance.svg" alt="rebalance" />
      <IconBot src="/images/icons/icon_bot_dca.svg" alt="dca" /> */}
    </div>
    <PlusCircleIcon />
  </div>
);

// Main Component
const BotSection = () => {
  const { botLists, loadingBotLists, setOpenDialogBot, loadingGetExchange } =
    useBot();

  return (
    <div className="bg-slate-100 w-full">
      <div className="flex justify-between items-start mt-3 relative ">
        {botLists?.length ? (
          <div className="w-full">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 my-0 py-0">
              Your Active Bots
            </h2>
            <p className="text-sm md:text-base text-[#21272A]/60 mt-1 py-0 mb-5 ">
              Here are your running bots and their performance. Add more bots
              anytime to diversify your strategies.
            </p>
          </div>
        ) : (
          <div className="w-full">
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 my-0 py-0">
              Activate Your First Bot
            </h2>
            <p className="text-sm md:text-base text-[#21272A]/60 mt-1 py-0 mb-5">
              No active bots yet. Choose a strategy and let your first bot start
              trading for you.
            </p>
          </div>
        )}

        <Link
          to={"/bot-list"}
          className="max-md:absolute max-md:top-0 max-md:right-0 text-sm flex-shrink-0 text-teal-600 hover:text-teal-700 font-medium cursor-pointer mr-1"
        >
          See All
        </Link>
      </div>

      {loadingBotLists || loadingGetExchange ? (
        <div className="flex flex-col md:flex-row md:space-x-4 pb-2">
          <Swiper
            modules={[Navigation]}
            spaceBetween={16}
            slidesPerView={1}
            navigation
            breakpoints={{
              0: { slidesPerView: 1.2 }, // for very small devices (mobile)
              480: { slidesPerView: 1.5 }, // slightly wider phones
              640: { slidesPerView: 2 }, // small tablets
              768: { slidesPerView: 2.2 }, // tablets
              1024: { slidesPerView: 3 }, // small laptops
              1440: { slidesPerView: 3.2 }, // desktops
              1720: { slidesPerView: 4 }, // desktops
            }}
            className="mt-4 md:mt-0 w-full"
          >
            <style>{`
              .swiper-button-next,
              .swiper-button-prev {
                display: none !important;
              }
            `}</style>
            {[...Array(8)].map((_, idx) => (
              <SwiperSlide key={idx}>
                <CardActiveBotSkeleton />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : botLists.length ? (
        <div className="flex flex-col md:flex-row md:space-x-4 pb-2">
          <ActivateNewBotCard setOpenDialogBot={setOpenDialogBot} />
          <Swiper
            modules={[Navigation]}
            spaceBetween={16}
            slidesPerView={1}
            navigation
            breakpoints={{
              0: { slidesPerView: botLists.length > 1 ? 1.2 : 1 }, // for very small devices (mobile)
              480: { slidesPerView: botLists.length > 1 ? 1.5 : 1 }, // slightly wider phones
              640: { slidesPerView: 2 }, // small tablets
              768: { slidesPerView: botLists.length > 2 ? 2.2 : 2 }, // tablets
              1024: { slidesPerView: 3 }, // small laptops
              1440: { slidesPerView: 3.2 }, // desktops
              1720: { slidesPerView: 4 }, // desktops
            }}
            className="mt-4 md:mt-0 w-full"
          >
            <style>{`
              .swiper-button-next,
              .swiper-button-prev {
                display: none !important;
              }
            `}</style>
            {botLists.map((bot, index) => (
              <SwiperSlide key={index}>
                <CardActiveBot bot={bot} />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      ) : (
        <div
          className="bg-white/80 backdrop-blur-sm p-8 rounded-xl flex flex-col items-center justify-center border border-dashed border-gray-300 text-center cursor-pointer hover:bg-gray-100"
          onClick={() => setOpenDialogBot("step-bot-1")}
        >
          <p className="text-base font-medium text-gray-700 mb-3">
            Activate New Bot
          </p>
          <div className="flex -space-x-4 mb-4">
            <BotLogo
              iconColor="bg-green-200"
              className="w-[45px] h-[45px] !justify-center !flex !items-center"
              size={42}
            />
            <BotLogo
              iconColor="bg-[#FAC482]"
              className="w-[45px] h-[45px] !justify-center !flex !items-center"
              size={42}
            />
            <BotLogo
              iconColor="bg-[#B2B7DE]"
              className="w-[45px] h-[45px] !justify-center !flex !items-center"
              size={42}
            />
          </div>
          <PlusCircleIcon />
        </div>
      )}
    </div>
  );
};

export default BotSection;
